import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import { Auth } from "aws-amplify";
import Box from "@mui/material/Box";
import { Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import Toast from "../../utils/Toast";
import LoadingCircle from "../../utils/LoadingCircle";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
import ReservTimeList from "../components/ReservTimeList";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";
import ReservRoomList from "../components/ReservRoomList";
import GolfRenewalNoticeDialog from "../components/GolfRenewalNoticeDialog";
const mainApi = new HttpMainApi();
interface propsType {
  userState: userState;
}
interface ReservationDetail {
  deviceNum: string;
  reservTime: string;
  reservDt: string;
  userName: string;
  userId: string;
  inputDt: string;
}
interface Reservation {
  sk: string;
  pk: string;
  reservations: { [key: string]: ReservationDetail };
}

export type GolfDevice = {
  deviceId: string;
  label: string;
  isRepair: boolean;
  availabilityStatus?: "OPERATING" | "UNDER_REPAIR" | "OPENING_SOON";
  repairReason?: string;
  updatedAt?: string;
  order?: number;
};

const getDeviceTabLabel = (device: GolfDevice) => {
  if (device.availabilityStatus === "OPENING_SOON") return "오픈 예정";
  if (device.availabilityStatus === "UNDER_REPAIR" || device.isRepair) return "수리중";
  return device.label;
};

type MyReservationHistoryItem = {
  reservState?: unknown;
  useTime?: unknown;
  reservInfo?: {
    pk?: unknown;
    sk?: unknown;
    reservations?: Record<string, { deviceNum?: unknown }>;
  };
};

type ScreenReservationDay = {
  bayMinutes: number;
  hasBay: boolean;
  hasRoom: boolean;
};

const formatCalendarDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

const formatCalendarMonthKey = (date: Date) => formatCalendarDateKey(date).slice(0, 6);

const getScreenReservationDay = (item: MyReservationHistoryItem, monthKey: string) => {
  if (item.reservState !== "신청" || typeof item.reservInfo?.pk !== "string") return null;

  const match = item.reservInfo.pk.match(/^(\d{8})\/SCREEN$/);
  if (!match || !match[1].startsWith(monthKey)) return null;

  const dateKey = match[1];
  const year = Number(dateKey.slice(0, 4));
  const month = Number(dateKey.slice(4, 6));
  const day = Number(dateKey.slice(6, 8));
  const date = new Date(year, month - 1, day);

  if (formatCalendarDateKey(date) !== dateKey) return null;

  const reservationTimes =
    typeof item.useTime === "string"
      ? item.useTime.split(",")
      : Object.keys(item.reservInfo.reservations ?? {}).flatMap((time) => time.split(","));
  const timeCount = reservationTimes.map((time) => time.trim()).filter((time) => /^\d{2}:\d{2}$/.test(time)).length;
  if (timeCount === 0) return null;

  const isRoom =
    item.reservInfo.sk === "ROOM" ||
    Object.values(item.reservInfo.reservations ?? {}).some((reservation) => reservation.deviceNum === "ROOM");

  return {
    dateKey,
    minutes: timeCount * (isRoom ? 30 : 60),
    facility: isRoom ? "room" : "bay",
  };
};

const getReservationDurationLevel = (minutes: number) => (minutes >= 180 ? 3 : minutes >= 120 ? 2 : 1);

const formatReservationDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0 ? `${hours}시간` : `${hours > 0 ? `${hours}시간 ` : ""}${remainingMinutes}분`;
};

const ReservationScreen = (props: propsType) => {
  const toastRef: any = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState<string[]>([]);
  const [stopDay, setStopDay] = useState<string[]>([]);
  const [stopDayMemo, setStopDayMemo] = useState("");
  const [devices, setDevices] = useState<GolfDevice[]>([]);
  const [isTimeLoading, setIsTimeLoading] = useState(false);
  const reservationRequestIdRef = useRef(0);
  const visibleDevices = useMemo(
    () => devices.filter((device) => ["타석1", "타석2", "타석3", "타석4", "타석5", "ROOM"].includes(device.deviceId)),
    [devices]
  );

  const today = new Date()
    .toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/. /g, "-")
    .replace(".", "");
  function createMarkup(text: string): JSX.Element[] {
    const paragraphs = text.split(/<br><\/br>/gi);
    return paragraphs.map((paragraph, index) => (
      <Fragment key={index}>
        {paragraph}
        {index < paragraphs.length - 1 && <br />}
      </Fragment>
    ));
  }

  const isStopday = stopDay.includes(today);
  const isHoliday = holidays.includes(today);

  const get_holiday_list = async () => {
    setIsLoading(true);
    const pk = "COMMUNITY/CLOSE";
    const sk = "HOLIDAY";
    const param: any = {
      pk: pk,
      sk: sk,
    };
    // console.log("param : ", param);
    const res = await mainApi.get_holiday_list(param);
    if (res.code === "200") {
      // setHolidays(res.response.result[0]["holidays"]);
      setStopDay(res.response.result[0]["temporary"]);
      setStopDayMemo(res.response.result[0]["memo"]);
      // console.log("holidays:", res.response.result[0]["holidays"]);
      // console.log("temporary:", res.response.result["temporary"]);
    } else {
      alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
    }
    setIsLoading(false);
  };

  const get_repair_list = async () => {
    setIsLoading(true);
    const pk = "COMMUNITY/REPAIR";
    const sk = "GOLF";
    const param: any = {
      pk: pk,
      sk: sk,
    };
    // console.log("param : ", param);
    const res = await mainApi.get_holiday_list(param);
    if (res.code === "200") {
      const devices = res.response?.result?.[0]?.devices || [];

      setDevices(devices);
    } else {
      alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  type ValuePiece = Date | null;
  type Value = ValuePiece | [ValuePiece, ValuePiece];
  const [value, onChange] = useState<Value>(new Date());
  const [activeCalendarMonth, setActiveCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [screenReservationDays, setScreenReservationDays] = useState<Map<string, ScreenReservationDay>>(() => new Map());
  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 오늘 날짜의 시간을 00:00:00으로 설정
    return date < today; // 오늘보다 이전 날짜인 경우 true 반환
  };
  const isToday = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  };
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    return `${year}${formattedMonth}${formattedDay}`;
  };

  const formatDate2 = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    return `${year}년 ${formattedMonth}월 ${formattedDay}일`;
  };
  const activeCalendarMonthKey = formatCalendarMonthKey(activeCalendarMonth);
  const todayCalendarDateKey = formatCalendarDateKey(new Date());

  useEffect(() => {
    let isCurrentRequest = true;

    const loadScreenReservationDates = async () => {
      const res = await mainApi.get_my_account_all_list({
        pk: props.userState.userId,
        sk: activeCalendarMonthKey,
        selectState: "ALL",
      });

      if (!isCurrentRequest) return;

      if (res.code !== "200" || !Array.isArray(res.response?.result)) {
        setScreenReservationDays(new Map());
        return;
      }

      const reservationDays = new Map<string, ScreenReservationDay>();
      res.response.result.forEach((item: MyReservationHistoryItem) => {
        const reservationDay = getScreenReservationDay(item, activeCalendarMonthKey);
        if (!reservationDay) return;

        const currentDay = reservationDays.get(reservationDay.dateKey) ?? {
          bayMinutes: 0,
          hasBay: false,
          hasRoom: false,
        };
        if (reservationDay.facility === "bay") {
          currentDay.bayMinutes += reservationDay.minutes;
        }
        currentDay.hasBay = currentDay.hasBay || reservationDay.facility === "bay";
        currentDay.hasRoom = currentDay.hasRoom || reservationDay.facility === "room";
        reservationDays.set(reservationDay.dateKey, currentDay);
      });
      setScreenReservationDays(reservationDays);
    };

    loadScreenReservationDates();

    return () => {
      isCurrentRequest = false;
    };
  }, [activeCalendarMonthKey, props.userState.userId]);

  const handleCalendarMonthChange = ({ activeStartDate, view }: { activeStartDate: Date | null; view: string }) => {
    if (view !== "month" || !activeStartDate) return;
    setActiveCalendarMonth(new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1));
  };

  const getCalendarTileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== "month") return null;

    const dateKey = formatCalendarDateKey(date);
    const reservationDay = screenReservationDays.get(dateKey);
    if (!reservationDay) return null;

    const stateClass = dateKey <= todayCalendarDateKey ? "hp-calendar__tile--completed" : "hp-calendar__tile--scheduled";
    const stampClass = reservationDay.hasBay && !reservationDay.hasRoom
      ? `hp-calendar__tile--duration-${getReservationDurationLevel(reservationDay.bayMinutes)}`
      : "hp-calendar__tile--black-stamp";
    return `${stateClass} ${stampClass}`;
  };

  const renderCalendarStamp = ({ date, view }: { date: Date; view: string }) => {
    if (view !== "month") return null;

    const dateKey = formatCalendarDateKey(date);
    const reservationDay = screenReservationDays.get(dateKey);
    if (!reservationDay) return null;

    const isCompleted = dateKey <= todayCalendarDateKey;
    const facilityLabel = reservationDay.hasBay && reservationDay.hasRoom
      ? `타석 ${formatReservationDuration(reservationDay.bayMinutes)}, 룸`
      : reservationDay.hasBay
        ? `타석 ${formatReservationDuration(reservationDay.bayMinutes)}`
        : "룸";
    const label = `${facilityLabel} 골프 예약 ${isCompleted ? "기록" : "예정"}`;
    const stateClass = isCompleted ? "is-completed" : "is-scheduled";

    return (
      <>
        <span aria-hidden="true" className="hp-calendar__stamp-set hp-calendar__stamp-set--center">
          <span className={`hp-calendar__stamp hp-calendar__stamp--ball hp-calendar__stamp--rubber ${stateClass}`} />
        </span>
        <span className="hp-sr-only">{label}</span>
      </>
    );
  };
  function formatDate3(date: Date) {
    const year = date.getFullYear();
    // getMonth()는 0부터 시작하므로 1을 더해줍니다.
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const fn_onClickDay = (newDate: Date) => {
    const year = newDate.getFullYear();
    const month = newDate.getMonth() + 1;
    const day = newDate.getDate();
    // 두 자리수로 포매팅
    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    // YYYYMMDD 형식으로 결합
    const formattedDate = `${year}${formattedMonth}${formattedDay}`;
    const formattedDate2 = `${year}년 ${formattedMonth}월 ${formattedDay}일`;
    setSelectDate(formattedDate);
    setSelectformatDate(formattedDate2);
  };

  function isDisabled(date: Date): boolean {
    return false; // 365일 영업
  }

  // function isDisabled(date: Date): boolean {
  //   const dayOfWeek = date.getDay();
  //   const dayOfMonth = date.getDate();
  //   const isFirstDayOfMonth = dayOfMonth === 1;
  //   const isMayFirst = date.getMonth() === 4 && dayOfMonth === 1;
  //   const isJanuaryFirst = date.getMonth() === 0 && dayOfMonth === 1;
  //   const isMarFirst = date.getMonth() === 2 && dayOfMonth === 1;
  //   //임시 공휴일 있을때 사용
  //   // const isOctFirst = date.getMonth() === 9 && dayOfMonth === 1; //2024년 10월 1일 임시 추가
  //   const formattedDate = formatDate3(date);
  //   // 매월 1일이 토요일 또는 일요일인 경우
  //   if (
  //     isFirstDayOfMonth &&
  //     (dayOfWeek === 6 ||
  //       dayOfWeek === 0 ||
  //       isMayFirst ||
  //       isJanuaryFirst ||
  //       // isOctFirst||
  //       isMarFirst)
  //   ) {
  //     return false; // 휴무일이 아님
  //   }

  //   // 매월 1일이 아닌 경우
  //   if (!isFirstDayOfMonth) {
  //     return holidays.includes(formattedDate);
  //   }

  //   // 매월 1일이지만 토요일 또는 일요일이 아닌 경우

  //   return true; // 휴무일
  // }

  function findFirstValidDate() {
    const today = new Date();
    let date = new Date(today);
    let tempDate = "";
    if (isDisabled(date)) {
      tempDate = "";
    } else {
      tempDate = formatDate(date);
    }

    return tempDate;
  }

  const [selectedDevice, setSelectedDevice] = useState("타석1");
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectDate, setSelectDate] = useState(findFirstValidDate());
  const [selectformatDate, setSelectformatDate] = useState(formatDate2(new Date()));
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isReservationConfirmOpen, setIsReservationConfirmOpen] = useState(false);
  const [isReservationSubmitting, setIsReservationSubmitting] = useState(false);

  const handleButtonClick = (deviceId: string) => {
    const target = visibleDevices.find((d) => d.deviceId === deviceId);
    if (!target || target.isRepair) return;
    setIsPageLoading(true); // ⭐ 화면 전체 잠금
    setSelectedTime(null);
    setSelectedTimes([]);
    setSelectedDevice(deviceId);
    setHole("60");
    setPeople("1");
    setUseTime(60);
    setMaxTime(2);
  };

  const handleTimeButtonClick = (time: any) => {
    setSelectedTime(time);
  };

  const reservation_save = async () => {
    const tempDate = await findFirstValidDate();
    console.log("tempDate : ", tempDate);
    if (tempDate === "" || tempDate === undefined || tempDate === null) {
      toastRef.current?.toast("커뮤니티센터 휴무일입니다.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }

    if (golfMember === false) {
      toastRef.current?.toast("[회원전용]골프연습장 신청 후 예약이 가능합니다.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }
    if (selectedDevice === "" || selectedDevice === undefined || selectedDevice === null) {
      toastRef.current?.toast("타석을 선택하세요.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }
    const selectedDeviceInfo = visibleDevices.find((device) => device.deviceId === selectedDevice);
    if (!selectedDeviceInfo || selectedDeviceInfo.isRepair) {
      toastRef.current?.toast("예약 가능한 타석을 선택하세요.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }
    if (tempDate === "" || tempDate === undefined || tempDate === null) {
      toastRef.current?.toast("날짜를 선택하세요.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }
    if (selectedTime === "" || selectedTime === undefined || (selectedTime === null && selectedTimes.length === 0)) {
      toastRef.current?.toast("시간을 선택하세요.", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
      return;
    }
    if (selectedTimes.length > 0) {
      if (selectedTimes.length !== maxTime) {
        alert(useTime + "분이 필요합니다.\n선택된 시간과 최대 예약 가능 시간이 일치하지 않습니다.");
        return;
      }

      const convertTimeToMinutes = (time: string) => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // 선택된 시간을 분 단위로 정렬
      const sortedTimes = [...selectedTimes].sort((a, b) => convertTimeToMinutes(a) - convertTimeToMinutes(b));

      // 연속된 시간인지 확인
      const isConsecutive = sortedTimes.every((time, index, array) => {
        if (index === 0) return true; // 첫 번째 시간은 항상 '연속적'
        return convertTimeToMinutes(time) - convertTimeToMinutes(array[index - 1]) === 30;
      });

      if (!isConsecutive) {
        alert("선택된 시간이 연속되지 않습니다.");
        return;
      }
    }
    setIsReservationConfirmOpen(true);
  };

  const confirmReservationSave = async () => {
    if (isReservationSubmitting) return;

    setIsReservationSubmitting(true);
    const { formattedDateTime, formathourspk, formatmonthpk } = getFormattedDateTime();
    const userId = props.userState.userId;
    const userAddr = props.userState.readAddress;
    const userName = props.userState.userName;
    const pk = `${selectDate}/SCREEN`;
    const mypk = `${formatmonthpk}/SCREEN`;
    let TempCommunityPrice = communityPrice9;
    let screenHole = "";

    if (selectedDevice !== "ROOM") {
      TempCommunityPrice = 0;
      screenHole = selectedDevice;
    } else if (selectedTimes.length > 0) {
      if (hole === "30") {
        screenHole = "9홀";
        TempCommunityPrice = communityPrice9 * selectedTimes.length;
      } else {
        screenHole = "18홀";
        TempCommunityPrice = Math.round((communityPrice18 * selectedTimes.length) / 2);
      }
    }

    const param: any = {
      pk: pk,
      sk: selectedDevice,
      option: selectedTime,
      userId: userId,
      userAddr: userAddr,
      userName: userName,
      inputDt: formattedDateTime,
      reservDt: selectformatDate,
      my_pk: mypk,
      my_sk: selectDate + formathourspk,
      communityPrice: TempCommunityPrice,
      info_01: screenHole,
    };

    try {
      const res = await mainApi.put_month_reserv_data(param);
      if (res.code === "200") {
        setIsReservationConfirmOpen(false);
        navigate("/reservationscreenend");
      } else if (res.code === "301") {
        alert("이미 예약된 시간입니다.\n다시 조회 후 진행해주세요.");
      } else {
        alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
      }
    } finally {
      setIsReservationSubmitting(false);
    }
  };

  const [times, setTimes] = useState<string[]>([]);

  const extractReservationTimes = (reservationData: Reservation[]): string[] => {
    return reservationData.flatMap((item) => Object.keys(item.reservations).flatMap((time) => time.split(",")));
  };

  const get_day_reservation = async () => {
    const requestId = ++reservationRequestIdRef.current;
    setIsTimeLoading(true);

    const pk = `${selectDate}/SCREEN`;
    const sk = selectedDevice;

    const param: any = { pk, sk };
    const res = await mainApi.get_day_reservation(param);

    if (requestId !== reservationRequestIdRef.current) {
      return;
    }

    if (res.code === "200") {
      const extractedTimes = extractReservationTimes(res.response.result);
      setTimes(extractedTimes);
    } else {
      setTimes([]); // 실패 시에도 비워서 안전
    }

    setIsTimeLoading(false);
  };

  const [golfMember, setGolfMember] = useState(true);
  const get_my_reservation = async () => {
    setIsLoading(true);

    const formatmonthpk = selectDate.substring(0, 6);
    const userId = props.userState.userId;
    const pk = userId;
    const sk = formatmonthpk;
    const param: any = {
      pk: pk,
      sk: sk,
      reservState: "신청",
      reservFilter: `${formatmonthpk}/GOLF`,
    };

    const res = await mainApi.get_my_reservation_diff_check(param);
    if (res.code === "200") {
      setGolfMember(true);
    } else if (res.code === "400") {
      setGolfMember(false);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const loadAll = async () => {
      setIsPageLoading(true);

      // ⭐ 핵심: 시간 리스트 즉시 초기화
      setIsTimeLoading(true);
      setTimes([]); // 이전 예약 데이터 제거

      await Promise.all([
        get_holiday_list(),
        get_repair_list(),
        get_day_reservation(),
        get_my_reservation(),
      ]);

      setIsTimeLoading(false); // ⭐ 시간 로딩 끝
      setIsPageLoading(false);
    };

    if (selectedDevice && selectDate) {
      loadAll();
    }
  }, [selectedDevice, selectDate]);

  //ROOM
  const [people, setPeople] = useState("1");
  const [hole, setHole] = useState("60");
  const [useTime, setUseTime] = useState(60);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [maxTime, setMaxTime] = useState(2);
  const handleChange = (event: SelectChangeEvent) => {
    setPeople(event.target.value as string);
    const peopleString = event.target.value as string;
    const peopleNumber = parseInt(peopleString, 10);
    const holeNumber = parseInt(hole, 10);
    setUseTime(peopleNumber * holeNumber);
    setMaxTime(Math.ceil((peopleNumber * holeNumber) / 30));
    setSelectedTimes([]);
  };

  const handleHoleChange = (event: SelectChangeEvent) => {
    setHole(event.target.value as string);
    const holeString = event.target.value as string;
    const holeNumber = parseInt(holeString, 10);
    const peopleNumber = parseInt(people, 10);
    setUseTime(peopleNumber * holeNumber);
    setMaxTime(Math.ceil((peopleNumber * holeNumber) / 30));
    setSelectedTimes([]);
  };

  // 시간 버튼 클릭 핸들러입니다.
  const handleRoomButtonClick = (timeValue: any) => {
    setSelectedTimes((prevTimes) => {
      if (prevTimes.length >= maxTime && !prevTimes.includes(timeValue)) {
        alert("최대 예약 시간을 초과했습니다.");
        return prevTimes; // 현재 상태를 유지
      }
      const newTimes = prevTimes.includes(timeValue)
        ? prevTimes.filter((time) => time !== timeValue)
        : [...prevTimes, timeValue];

      // 새로운 배열을 문자열로 변환하여 setSelectedTime에 설정
      const timeTemp = newTimes.join(",");
      setSelectedTime(timeTemp);

      return newTimes;
    });
  };

  const [communityPrice9, setCommunityPrice9] = useState(0);
  const [communityPrice18, setCommunityPrice18] = useState(0);
  const get_price9 = async () => {
    const pk = "COMMUNITY/ACCOUNT";
    const sk = "스크린9홀";
    const param: any = {
      pk: pk,
      sk: sk,
    };
    const res = await mainApi.get_price(param);
    if (res.code === "200") {
      setCommunityPrice9(res.response.result[0]["PRICE"]);
    } else {
      alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
    }
  };
  const get_price18 = async () => {
    const pk = "COMMUNITY/ACCOUNT";
    const sk = "스크린18홀";
    const param: any = {
      pk: pk,
      sk: sk,
    };
    const res = await mainApi.get_price(param);
    if (res.code === "200") {
      setCommunityPrice18(res.response.result[0]["PRICE"]);
    } else {
      alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
    }
  };
  const [reserv_am, setReserv_am] = useState<string[]>([]);
  const [reserv_pm, setReserv_pm] = useState<string[]>([]);
  const [reserv_room, setReserv_room] = useState<string[]>([]);

  const get_date_time = async () => {
    // 화요일부터 금요일까지
    setReserv_am(["05:00", "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00"]);
    setReserv_pm(["13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00"]);
    setReserv_room([
      "05:00",
      "05:30",
      "06:00",
      "06:30",
      "07:00",
      "07:30",
      "08:00",
      "08:30",
      "09:00",
      "09:30",
      "10:00",
      "10:30",
      "11:00",
      "11:30",
      "12:00",
      "12:30",
      "13:00",
      "13:30",
      "14:00",
      "14:30",
      "15:00",
      "15:30",
      "16:00",
      "16:30",
      "17:00",
      "17:30",
      "18:00",
      "18:30",
      "19:00",
      "19:30",
      "20:00",
      "20:30",
      "21:00",
      "21:30",
      "22:00",
      "22:30",
    ]);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    get_price9();
    get_price18();
    get_date_time();
  }, []);

  useEffect(() => {
    if (visibleDevices.length > 0) {
      const selectedDeviceInfo = visibleDevices.find((d) => d.deviceId === selectedDevice);
      if (selectedDeviceInfo && !selectedDeviceInfo.isRepair) {
        return;
      }

      const firstAvailable = visibleDevices.find((d) => !d.isRepair);
      if (firstAvailable && firstAvailable.deviceId !== selectedDevice) {
        setSelectedTime(null);
        setSelectedTimes([]);
        setSelectedDevice(firstAvailable.deviceId);
      }
    }
  }, [visibleDevices, selectedDevice]);

  return (
    <>
      <Stack
        className="hpoint-renewal hp-reservation-page"
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : "40rem",
          margin: "auto",
        }}
      >
        <Box
          className="hp-screen-hero"
          sx={{
            width: "100%",
            minWidth: "360px",
          }}
        >
          <Typography
            className="hp-page-title"
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 300,
              textAlign: "left",
              margin: "auto",
              mt: 2,
              ml: 3,
            }}
          >
            예약정보
          </Typography>
          <Typography className="hp-page-description">
            오늘 날짜와 이용할 타석, 시간을 차례대로 선택해 주세요.
          </Typography>
        </Box>

        <hr className="hp-divider" />

        <Box
          className="hp-card hp-calendar-card"
          sx={{
            width: "90%",
            maxWidth: isMobile ? "100%" : "40rem",
            margin: "auto",
          }}
        >
          <Box className="hp-calendar-history" role="status">
            <Typography className="hp-calendar-history__label">
              {activeCalendarMonth.getMonth() + 1}월 스크린골프 예약
            </Typography>
            <Typography className="hp-calendar-history__count">
              {screenReservationDays.size}일
            </Typography>
            <Typography component="div" className="hp-calendar-history__legend" data-testid="calendar-history-legend">
              <span className="hp-calendar-history__facility-label">타석</span>
              <span className="hp-calendar-history__duration-list">
                <span aria-hidden="true" className="hp-calendar-history__ball hp-calendar-history__ball--duration-1" />
                <span>1시간</span>
                <span aria-hidden="true" className="hp-calendar-history__ball hp-calendar-history__ball--duration-2" />
                <span>2시간</span>
                <span aria-hidden="true" className="hp-calendar-history__ball hp-calendar-history__ball--duration-3" />
                <span>3시간+</span>
              </span>
              <span className="hp-calendar-history__facility-label">룸</span>
              <span aria-hidden="true" className="hp-calendar-history__ball hp-calendar-history__ball--room" />
            </Typography>
          </Box>
          {/* 오늘 이후만 예약 가능 
          <Calendar
            onChange={onChange}
            onClickDay={fn_onClickDay}
            value={value}
            className="custom-calendar hp-calendar"
            formatDay={(_locale, date) => String(date.getDate())}
            tileDisabled={({ date, view }) =>
              view === "month" && isPastDate(date)
            }
          />
           */}
          {/* 오늘만 예약가능 */}
          <Calendar
            onChange={onChange}
            onClickDay={fn_onClickDay}
            value={value}
            calendarType="gregory"
            className="custom-calendar hp-calendar"
            formatDay={(_locale, date) => String(date.getDate())}
            activeStartDate={activeCalendarMonth}
            onActiveStartDateChange={handleCalendarMonthChange}
            tileClassName={getCalendarTileClassName}
            tileContent={renderCalendarStamp}
            tileDisabled={
              ({ date, view }) => !isToday(date) // 오늘이 아닌 날짜 비활성화
              //|| isDisabled(date)              // 공휴일 비활성화, 단, 오늘 날짜는 예외 처리
            }
          />
        </Box>

        <hr className="hp-divider" />

        <ButtonGroup className="hp-segmented-control" size="large" aria-label="골프 타석 선택">
          {visibleDevices.map((device) => {
            const isSelected = selectedDevice === device.deviceId;
            const isDisabled = device.isRepair;

            return (
              <Button
                key={device.deviceId}
                disabled={isDisabled}
                aria-pressed={isSelected}
                sx={{
                  borderColor: "#2E3341",

                  // 기본
                  color: isSelected ? "#ffffff" : "#2E3341",
                  backgroundColor: isSelected ? "#2E3341" : "transparent",

                  // hover
                  "&:hover": {
                    color: isSelected ? "#ffffff" : "#2E3341",
                    backgroundColor: isSelected ? "#2E3341" : "rgba(46,51,65,0.04)",
                  },

                  // active (클릭 중)
                  "&:active": {
                    color: isSelected ? "#ffffff" : "#2E3341",
                    backgroundColor: isSelected ? "#2E3341" : "rgba(46,51,65,0.08)",
                  },

                  // focus
                  "&.Mui-focusVisible": {
                    color: isSelected ? "#ffffff" : "#2E3341",
                    backgroundColor: isSelected ? "#2E3341" : "transparent",
                  },

                  // ButtonGroup 내부 버튼
                  "&.MuiButtonGroup-grouped": {
                    borderColor: "#2E3341",
                  },

                  opacity: isDisabled ? 0.5 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",

                  // disabled
                  "&.Mui-disabled": {
                    color: isSelected ? "#ffffff" : "#2E3341",
                    backgroundColor: isSelected ? "#2E3341" : "transparent",
                    opacity: 0.5,
                  },
                }}
                onClick={() => handleButtonClick(device.deviceId)}
              >
                {getDeviceTabLabel(device)}
              </Button>
            );
          })}
        </ButtonGroup>

        {isStopday ? (
          <Box
            className="hp-notice-panel hp-notice-panel--danger"
            sx={{
              width: "90%",
              textAlign: "center",
            }}
          >
            <Typography
              variant="button"
              gutterBottom
              sx={{
                textAlign: "left",
                margin: "auto",
                whiteSpace: "pre-line",
                color: "red", // 붉은색 강조
              }}
            >
              * 골프연습장 점검중입니다.
            </Typography>
            <Alert
              severity="error"
              icon={false}
              sx={{
                textAlign: "left", // 텍스트를 좌측으로 정렬
                alignItems: "flex-start", // 내용을 상단에 정렬
              }}
            >
              {createMarkup(stopDayMemo)}
            </Alert>
          </Box>
        ) : isHoliday ? (
          <Box
            className="hp-notice-panel hp-notice-panel--danger"
            sx={{
              width: "90%",
              textAlign: "center",
            }}
          >
            <Typography
              variant="button"
              gutterBottom
              sx={{
                textAlign: "left",
                margin: "auto",
                whiteSpace: "pre-line",
                color: "red",
              }}
            >
              * 오늘은 커뮤니티센터 휴무일입니다.
            </Typography>
            <Alert
              severity="error"
              icon={false}
              sx={{
                textAlign: "left", // 텍스트를 좌측으로 정렬
                alignItems: "flex-start", // 내용을 상단에 정렬
              }}
            >
              매월 1일은 휴무입니다. <br></br>만약 1일이 공휴일인 경우, 다음 근무일이 휴무일이 됩니다.
              <br></br>휴무일에는 시설물 정비를 진행합니다.
            </Alert>
          </Box>
        ) : (
          <Box
            className="hp-notice-panel"
            sx={{
              width: "90%",
              textAlign: "center",
            }}
          >
            <Typography
              variant="button"
              gutterBottom
              sx={{
                textAlign: "left",
                margin: "auto",
                whiteSpace: "pre-line",
              }}
            >
              * 스크린 타석과 ROOM은 당일 예약만 가능합니다.
            </Typography>
            <Alert
              severity="info"
              icon={false}
              sx={{
                textAlign: "left", // 텍스트를 좌측으로 정렬
                alignItems: "flex-start", // 내용을 상단에 정렬
              }}
            >
              운영시간은 평일과 주말 모두 05:00부터 23:00입니다.
            </Alert>
          </Box>
        )}

        {selectedDevice === "ROOM" ? (
          <>
            <Box className="hp-selector-row" sx={{ display: "flex", gap: 3 }}>
              <Box className="hp-field" sx={{ minWidth: 120 }}>
                <FormControl className="hp-field" fullWidth>
                  <InputLabel id="demo-simple-select-label">인원</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={people}
                    label="인원"
                    onChange={handleChange}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value={"1"}>1명</MenuItem>
                    <MenuItem value={"2"}>2명</MenuItem>
                    <MenuItem value={"3"}>3명</MenuItem>
                    <MenuItem value={"4"}>4명</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box className="hp-field" sx={{ minWidth: 120 }}>
                <FormControl className="hp-field" fullWidth>
                  <InputLabel id="demo-simple-select-label">홀</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={hole}
                    label="홀"
                    onChange={handleHoleChange}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value={"30"}>9홀</MenuItem>
                    <MenuItem value={"60"}>18홀</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 20 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 300,
                    textAlign: "center",
                    margin: "auto",
                  }}
                >
                  {useTime} 분
                </Typography>
              </Box>
            </Box>
            <ReservRoomList
              userState={props.userState}
              reserv_time={reserv_room}
              handleRoomButtonClick={handleRoomButtonClick}
              selectedTimes={selectedTimes}
              chunkNum={4}
              times={times}
            />
          </>
        ) : (
          <>
            <Box
              className="hp-time-grid-wrap"
              sx={{
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 300,
                  textAlign: "left",
                  margin: "auto",
                  ml: 3,
                }}
              >
                오전
              </Typography>
            </Box>
            <Box
              className="hp-time-section-title"
              sx={{
                width: "100%",
              }}
            >
              <ReservTimeList
                userState={props.userState}
                reserv_time={reserv_am}
                handleTimeButtonClick={handleTimeButtonClick}
                selectedTime={selectedTime}
                chunkNum={4}
                times={times}
              />
            </Box>
            <Box
              className="hp-time-section-title"
              sx={{
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 300,
                  textAlign: "left",
                  margin: "auto",
                  ml: 3,
                }}
              >
                오후
              </Typography>
            </Box>
            <Box
              className="hp-time-grid-wrap"
              sx={{
                width: "100%",
              }}
            >
              <ReservTimeList
                userState={props.userState}
                reserv_time={reserv_pm}
                handleTimeButtonClick={handleTimeButtonClick}
                selectedTime={selectedTime}
                chunkNum={4}
                times={times}
              />
            </Box>
          </>
        )}
        <Stack
          className="hp-cta-wrap"
          direction="row"
          sx={{
            flexWrap: "wrap",
            justifyContent: "center",
            width: "95%", // 전체 너비를 사용하도록 설정
            height: "4em",
            marginTop: "3em",
          }}
        >
          <Button
            className="hp-button hp-button--primary hp-sticky-cta"
            sx={{
              borderRadius: "10px",
              border: "1px solid #ccc",
              width: "100%",
            }}
            color="primary"
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={() => reservation_save()}
          >
            <Typography variant="h6">예약 하기</Typography>
          </Button>
        </Stack>
      </Stack>
      <GolfRenewalNoticeDialog />
      <Dialog
        aria-labelledby="screen-reservation-confirm-title"
        className="hp-dialog"
        open={isReservationConfirmOpen}
        PaperProps={{ className: "hp-dialog__paper" }}
        onClose={() => {
          if (!isReservationSubmitting) setIsReservationConfirmOpen(false);
        }}
      >
        <DialogTitle id="screen-reservation-confirm-title">예약하시겠어요?</DialogTitle>
        <DialogContent>
          <DialogContentText>선택하신 예약 정보를 확인해 주세요.</DialogContentText>
          <DialogContentText component="div">{selectformatDate}</DialogContentText>
          <DialogContentText component="div">{selectedDevice}</DialogContentText>
          <DialogContentText component="div">{selectedTimes.length > 0 ? selectedTimes.join(", ") : selectedTime}</DialogContentText>
          {selectedDevice === "ROOM" && <DialogContentText>게임 시작 1시간 전부터는 취소할 수 없어요.</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button disabled={isReservationSubmitting} onClick={() => setIsReservationConfirmOpen(false)}>
            취소
          </Button>
          <Button disabled={isReservationSubmitting} onClick={confirmReservationSave}>
            예약 확정
          </Button>
        </DialogActions>
      </Dialog>
      <Toast ref={toastRef} />
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default ReservationScreen;
